import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";

// Отступ между полем и выпадающим списком
const GAP = 8;
// Минимальный отступ от краёв окна
const VIEWPORT_GAP = 8;
// Ниже этой высоты список не сжимаем — выгоднее открыть в другую сторону
const MIN_HEIGHT = 140;

const isSamePlacement = (a, b) => (
    a.dropUp === b.dropUp
    && a.maxHeight === b.maxHeight
    && a.anchorOffset === b.anchorOffset
    && a.shiftX === b.shiftX
);

/**
 * Считает, в какую сторону открывать выпадающий список и какой он может быть высоты,
 * чтобы не выходить за пределы окна по вертикали и горизонтали.
 *
 * @param {object}  options
 * @param {boolean} options.open      открыт ли список (пересчёт идёт, только когда открыт)
 * @param {boolean} options.preferTop предпочтительное направление до первого замера
 * @param {Array}   options.deps      значения, от которых зависит высота содержимого
 */
const useDropdownPosition = ({ open, preferTop = false, deps = [] }) => {
    const containerRef = useRef(null); // контейнер с position: relative
    const anchorRef = useRef(null);    // поле, относительно которого открывается список
    const listRef = useRef(null);      // сам выпадающий список

    const [placement, setPlacement] = useState({
        dropUp: preferTop,
        maxHeight: null,
        anchorOffset: 0,
        shiftX: 0,
    });

    const updatePosition = useCallback(() => {
        const container = containerRef.current;
        const anchor = anchorRef.current;
        const list = listRef.current;
        if (!container || !anchor || !list) return;

        const anchorRect = anchor.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight;
        const viewportW = window.innerWidth || document.documentElement.clientWidth;

        const spaceBelow = viewportH - anchorRect.bottom - GAP - VIEWPORT_GAP;
        const spaceAbove = anchorRect.top - GAP - VIEWPORT_GAP;

        // натуральная высота содержимого — меряем без ограничения по высоте
        const prevMaxHeight = list.style.maxHeight;
        list.style.maxHeight = "none";
        const naturalHeight = list.scrollHeight;
        list.style.maxHeight = prevMaxHeight;

        // помещается снизу — открываем вниз, иначе вверх,
        // а если не помещается нигде — туда, где места больше
        let dropUp;
        if (naturalHeight <= spaceBelow) dropUp = false;
        else if (naturalHeight <= spaceAbove) dropUp = true;
        else dropUp = spaceAbove > spaceBelow;

        const availableSpace = dropUp ? spaceAbove : spaceBelow;
        const maxHeight = Math.max(MIN_HEIGHT, Math.min(naturalHeight, availableSpace));

        // расстояние от края контейнера до края поля
        const anchorOffset = dropUp
            ? Math.round(containerRect.bottom - anchorRect.top + GAP)
            : Math.round(anchorRect.bottom - containerRect.top + GAP);

        // горизонтальный сдвиг меряем от несмещённого положения, чтобы он не накапливался
        const prevLeft = list.style.left;
        list.style.left = "0px";
        const listRect = list.getBoundingClientRect();
        list.style.left = prevLeft;

        let shiftX = 0;
        const overflowRight = listRect.right - (viewportW - VIEWPORT_GAP);
        if (overflowRight > 0) shiftX -= overflowRight;
        const overflowLeft = VIEWPORT_GAP - (listRect.left + shiftX);
        if (overflowLeft > 0) shiftX += overflowLeft;

        const next = { dropUp, maxHeight, anchorOffset, shiftX: Math.round(shiftX) };
        setPlacement(prev => (isSamePlacement(prev, next) ? prev : next));
    }, []);

    // пересчёт при открытии и при изменении содержимого
    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, updatePosition, ...deps]);

    // ...и при скролле/ресайзе, пока список открыт
    useEffect(() => {
        if (!open) return undefined;
        const handle = () => updatePosition();
        window.addEventListener("resize", handle);
        window.addEventListener("scroll", handle, true);
        return () => {
            window.removeEventListener("resize", handle);
            window.removeEventListener("scroll", handle, true);
        };
    }, [open, updatePosition]);

    return { containerRef, anchorRef, listRef, updatePosition, ...placement };
};

export default useDropdownPosition;
